import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import Box from '@mui/material/Box'
import CardMedia from '@mui/material/CardMedia'
import MuiIconButton from '@mui/material/IconButton'
import { styled, useTheme } from '@mui/material/styles'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import SwiperContainer from './SwiperContainer'

const IconButton = styled(MuiIconButton)(({ theme }) => ({
  opacity: '0.7',
  background: theme.palette.background.paper,
  '&.Mui-disabled': {
    background: theme.palette.background.paper,
  },
  '&:hover': {
    background: theme.palette.background.paper,
  },
  borderWidth: 1,
  borderColor: theme.palette.border.lightGrey,
  borderStyle: 'solid',
  aspectRatio: 1,
  width: 20,
  height: 20,
}))
const ExceptionCardMediaSwiperContainer = ({ images, children }) => {
  const swiperRef = useRef()
  const theme = useTheme()

  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  // Dereference the swiper instance INSIDE the handlers: reading it at render freezes a null
  // into the callback (the ref populates after first render with no re-render following), so
  // the arrows would be no-ops until an unrelated re-render.
  const handleSwiperPrev = useCallback(event => {
    event.stopPropagation()
    const swiper = swiperRef.current?.swiper
    swiper?.slidePrev(300)
    swiper?.update()
  }, [])

  const handleSwiperNext = useCallback(event => {
    // The card behind the arrows opens the detail drawer on click — don't let
    // paging bubble into it (the prev arrow already stops it).
    event.stopPropagation()
    const swiper = swiperRef.current?.swiper
    swiper?.slideNext(300)
    swiper?.update()
  }, [])

  const stopClickPropagation = useCallback(event => event.stopPropagation(), [])

  const handleReachEnd = useCallback(swiperInstance => {
    setIsEnd(swiperInstance.isEnd)
    setIsBeginning(swiperInstance.isBeginning)
  }, [])

  const handleSlideChange = useCallback(swiperInstance => {
    setIsEnd(swiperInstance.isEnd)
    setIsBeginning(swiperInstance.isBeginning)
  }, [])

  useEffect(() => {
    if (Array.isArray(images)) {
      setIsEnd(images.length === 1)
    }
  }, [images])

  return (
    <CardMedia alt="Paella dish" component="div" height="194" sx={{ position: 'relative' }}>
      <SwiperContainer
        observer
        swiperRef={swiperRef}
        sx={{
          '.swiper-pagination-bullet': {
            opacity: 0.5,
            background: theme.palette.common.white,
          },
          '.swiper-pagination-bullet-active': {
            opacity: 1,
            background: theme.palette.common.white,
          },
        }}
        onReachEnd={handleReachEnd}
        onSlideChange={handleSlideChange}
      >
        {children}
      </SwiperContainer>
      {Array.isArray(images) && images.length > 1 && (
        <>
          <Box
            bottom="calc(50% - 12px)"
            left="4px"
            margin="0 auto"
            marginRight="-28px"
            position="absolute"
            transform="translate(-50%, -50%)"
            zIndex={1}
            onClick={stopClickPropagation}
          >
            <IconButton disabled={isBeginning} onClick={handleSwiperPrev}>
              <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <Box
            bottom="calc(50% - 12px)"
            margin="0 auto"
            marginLeft="-28px"
            position="absolute"
            right="4px"
            transform="translate(-50%, -50%)"
            zIndex={1}
            onClick={stopClickPropagation}
          >
            <IconButton disabled={isEnd} sx={{ ml: 1 }} onClick={handleSwiperNext}>
              <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </>
      )}
    </CardMedia>
  )
}

ExceptionCardMediaSwiperContainer.propTypes = {
  images: PropTypes.array,
  children: PropTypes.node,
}

export default ExceptionCardMediaSwiperContainer
